import { ComboboxOption } from "@headlessui/react";
import { cn } from "~/lib/utils";
import {
    SearchWindowOptionRow,
    SearchWindowVirtualRowProps,
} from "../../types";
import { SearchResultIcon } from "./SearchIcon";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";

// // // //

/**
 * Renders a search result option row for the search window
 */
export function SearchWindowOption(
    props: {
        row: SearchWindowOptionRow;
    } & SearchWindowVirtualRowProps,
) {
    const { row, ...virtualRowProps } = props;
    const { item, altPrefix = item.content.label, showCategory } = row;

    // Determine the total value to display
    const totalValue =
        typeof item.totalValue === "number" &&
        Number.isFinite(item.totalValue) &&
        item.totalValue >= 0
            ? item.totalValue
            : null;

    return (
        <ComboboxOption
            {...virtualRowProps}
            as="div"
            // NOTE - the value is the row (not the item) because the Combobox
            // resolves the active option by its index in the virtualized rows
            value={row}
            className={({ focus }) =>
                cn("w-full cursor-pointer select-none px-4 py-2 gap-3", {
                    "flex items-center": !showCategory,
                    "grid grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-y-1 sm:flex":
                        showCategory,
                    // NOTE - focus state here matches SidebarNav
                    "bg-muted text-link": focus,
                })
            }
        >
            <SearchResultIcon
                src={item.content.icon}
                className={item.content.iconClassName}
                wrapperClassName={item.content.iconWrapperClassName}
                alt={`${altPrefix} Search Result Icon`}
            />

            <div className="flex min-w-0 flex-1 items-center gap-3">
                <p
                    className="max-w-full truncate text-base"
                    title={item.content.label}
                >
                    {item.content.label}
                </p>
                {item.content.subtitle && (
                    <p className="max-w-full shrink-0 truncate text-muted-foreground text-sm">
                        {item.content.subtitle}
                    </p>
                )}
            </div>

            {/* Show the total value and category on `md` screens and above (hidden on mobile) */}
            {showCategory && (
                <div className="hidden col-start-2 md:flex shrink-0 items-center justify-self-end gap-2">
                    {totalValue !== null && (
                        <span
                            className="whitespace-nowrap text-muted-foreground text-sm"
                            title={`Total value: $${totalValue.toLocaleString("en-US", { maximumFractionDigits: 2 })}`}
                        >
                            ${formatCurrencyValueCompact(totalValue)}
                        </span>
                    )}
                    <span
                        className="max-w-40 truncate rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                        title={item.category}
                    >
                        {item.category}
                    </span>
                </div>
            )}
        </ComboboxOption>
    );
}
